<template>
  <div class="home-container">
    <div class="welcome-section">
      <div class="welcome-content">
        <div class="welcome-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9,22 9,12 15,12 15,22"/>
          </svg>
        </div>
        <h1>Üdvözöljük!</h1>
        <p v-if="isAdmin || isTeacher">Admin felület - Csoportok és események kezelése</p>
        <p v-else-if="isParent">Szülői felület - Gyermekeim táncóráinak és adatainak kezelése</p>
        <p v-else-if="isDancer">Saját táncórák és versenyek</p>
        <p v-else>Táncosok és táncórák kezelése</p>
        <div v-if="isAdmin || isTeacher" class="stat-pill">Diák száma: <strong>{{ studentCount }}</strong></div>
      </div>
    </div>
    
    <div class="content-section">
      <div v-if="isAdmin || isTeacher" class="admin-content">
        <GroupsWithDate />
        <div v-if="upcomingCompetitions.length" class="competitions-section">
          <h2>Közelgő versenyek</h2>
          <div class="competition-list">
            <div v-for="comp in upcomingCompetitions" :key="comp.id" class="competition-card">
              <div class="comp-date">{{ comp.comp_date }}</div>
              <div class="comp-title">{{ comp.title }}</div>
              <div v-if="comp.location" class="comp-location">{{ comp.location }}</div>
            </div>
          </div>
        </div>
      </div> 
      <div v-else class="user-content">
        <MultipleChildItem />

        <!-- Upcoming Competitions, Galas, & Events for Dancers & Competitors -->
        <div v-if="upcomingCompetitions.length || upcomingGalas.length || calendarEvents.length" class="events-section mt-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h2 class="h4 font-weight-bold mb-0 text-primary d-flex align-items-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-2 text-warning">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Közelgő Események, Gálák és Versenyek
            </h2>
            <router-link to="/calendars" class="btn btn-outline-primary btn-sm">
              Eseménynaptár megtekintése &rarr;
            </router-link>
          </div>

          <div class="row">
            <!-- Competitions -->
            <div v-if="upcomingCompetitions.length" class="col-md-4 mb-3">
              <div class="card h-100 border-primary shadow-sm">
                <div class="card-header bg-primary text-white font-weight-bold d-flex align-items-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-2">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>
                  </svg>
                  Versenyek
                </div>
                <div class="card-body">
                  <div v-for="comp in upcomingCompetitions" :key="comp.id" class="mb-3 pb-2 border-bottom last-border-0">
                    <div class="badge bg-primary mb-1">{{ formatDateOnly(comp.comp_date) }}</div>
                    <div class="font-weight-bold">{{ comp.title }}</div>
                    <small class="text-muted d-block" v-if="comp.location">📍 {{ comp.location }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Galas -->
            <div v-if="upcomingGalas.length" class="col-md-4 mb-3">
              <div class="card h-100 border-purple shadow-sm" style="border-color: #a855f7;">
                <div class="card-header text-white font-weight-bold d-flex align-items-center" style="background-color: #a855f7;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  Gálák
                </div>
                <div class="card-body">
                  <div v-for="gala in upcomingGalas" :key="gala.id" class="mb-3 pb-2 border-bottom last-border-0">
                    <div class="badge bg-purple mb-1" style="background-color: #a855f7;">{{ formatDateOnly(gala.date) }}</div>
                    <div class="font-weight-bold">{{ gala.name }}</div>
                    <small class="text-muted d-block" v-if="gala.location">📍 {{ gala.location }}</small>
                    <router-link to="/public-galas" class="btn btn-sm btn-outline-purple mt-1" style="color: #a855f7; border-color: #a855f7;">
                      Részletek & Jegyek
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Calendar Events -->
            <div v-if="calendarEvents.length" class="col-md-4 mb-3">
              <div class="card h-100 border-info shadow-sm">
                <div class="card-header bg-info text-white font-weight-bold d-flex align-items-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Egyéb események
                </div>
                <div class="card-body">
                  <div v-for="evt in calendarEvents" :key="evt.id" class="mb-3 pb-2 border-bottom last-border-0">
                    <div class="badge bg-info mb-1">{{ formatDateOnly(evt.event_date) }}</div>
                    <div class="font-weight-bold">{{ evt.title }}</div>
                    <small class="text-muted d-block" v-if="evt.location">📍 {{ evt.location }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GroupsWithDate from './GroupsWithDate.vue';
import MultipleChildItem from './MultipleChildItem.vue';

export default {
  components: {
    GroupsWithDate,
    MultipleChildItem
  },
  data() {
    return {
      isAdmin: false,
      isTeacher: false,
      isAssistant: false,
      isDancer: false,
      isParent: false,
      studentCount: 0,
      upcomingCompetitions: [],
      upcomingGalas: [],
      calendarEvents: [],
    };
  },
  methods: {
    getIsAdmin() {
      this.isAdmin = this.$isAdmin();
    },
    getIsTeacher() {
      this.isTeacher = this.$isTeacher();
    },
    getIsDancer() {
      this.isDancer = this.$isDancer();
    },
    getIsParent() {
      this.isParent = this.$isParent();
    },
    async fetchStudentCount() {
      try {
        const r = await fetch(this.$BACKEND_HOST + '/api/dashboard/student-count');
        const d = await r.json();
        this.studentCount = d.student_count || 0;
      } catch (e) { console.error(e); }
    },
    async fetchEventsAndGalas() {
      try {
        const [cRes, gRes, eRes] = await Promise.all([
          fetch(this.$BACKEND_HOST + '/api/competitions/home'),
          fetch(this.$BACKEND_HOST + '/api/public/galas'),
          fetch(this.$BACKEND_HOST + '/api/calendar-events')
        ]);
        const cData = cRes.ok ? await cRes.json() : {};
        const gData = gRes.ok ? await gRes.json() : {};
        const eData = eRes.ok ? await eRes.json() : {};

        this.upcomingCompetitions = cData.events || [];
        this.upcomingGalas = gData.galas || [];
        this.calendarEvents = eData.events || [];
      } catch (e) { console.error(e); }
    },
    formatDateOnly(val) {
      if (!val) return '';
      let str = String(val).trim();
      if (str.includes('T')) {
        str = str.split('T')[0];
      } else if (str.includes(' ')) {
        str = str.split(' ')[0];
      }
      return str;
    },
  },
  mounted() {
    this.getIsAdmin();
    this.getIsTeacher();
    this.getIsDancer();
    this.getIsParent();
    this.fetchStudentCount();
    this.fetchEventsAndGalas();
  }
};
</script>

<style scoped>
.home-container {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.welcome-section {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  text-align: center;
  box-shadow: var(--shadow-lg);
  position: relative;
  overflow: hidden;
}

.welcome-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

.welcome-content {
  position: relative;
  z-index: 1;
}

.welcome-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.welcome-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-content p {
  font-size: 1.125rem;
  opacity: 0.9;
  margin: 0;
  font-weight: 500;
}

.content-section {
  flex: 1;
}

.admin-content,
.user-content {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.competitions-section {
  padding: 1.5rem;
  border-top: 1px solid var(--color-border);
}

.lessons-section {
  padding: 1.5rem;
  border-top: 1px solid var(--color-border);
}

.lessons-section h2 {
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
  color: var(--color-text);
}

.lesson-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.lesson-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--color-background-soft);
  border-radius: 10px;
  border: 1px solid var(--color-border);
}

.lesson-card__name {
  font-weight: 600;
  color: var(--color-text);
  flex: 1;
}

.lesson-card__details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.lesson-card__date {
  font-weight: 700;
  color: var(--primary-blue);
  white-space: nowrap;
  font-size: 0.9rem;
}

.lesson-card__schedule {
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.competitions-section h2 {
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
  color: var(--color-text);
}

.competition-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.competition-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--color-background-soft);
  border-radius: 10px;
  border: 1px solid var(--color-border);
}

.comp-date {
  font-weight: 700;
  color: var(--primary-blue);
  white-space: nowrap;
}

.comp-title {
  font-weight: 600;
  color: var(--color-text);
}

.comp-location {
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.stat-pill {
  margin-top: 1rem;
  display: inline-block;
  background: rgba(255,255,255,0.18);
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 1rem;
}

@media (max-width: 768px) {
  .home-container {
    gap: 1.5rem;
  }
  
  .welcome-section {
    padding: 1.5rem;
    border-radius: 12px;
  }
  
  .welcome-icon {
    width: 64px;
    height: 64px;
  }
  
  .welcome-content h1 {
    font-size: 2rem;
  }
  
  .welcome-content p {
    font-size: 1rem;
  }
  
  .admin-content,
  .user-content {
    border-radius: 12px;
  }
}

@media (max-width: 480px) {
  .welcome-section {
    padding: 1.25rem;
    border-radius: 10px;
  }
  
  .welcome-icon {
    width: 56px;
    height: 56px;
  }
  
  .welcome-content h1 {
    font-size: 1.75rem;
  }
  
  .welcome-content p {
    font-size: 0.9rem;
  }
}
</style>
